import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon as MenuListItemIcon,
  TextField,
  Card,
  CardContent
} from '@mui/material'
import {
  Person,
  Email,
  Phone,
  CalendarToday,
  Assignment,
  AttachMoney,
  MoreVert,
  Edit,
  CheckCircle,
  Cancel,
  Send,
  Receipt,
  Refresh,
  Close
} from '@mui/icons-material'
import { format, parseISO } from 'date-fns'
import { useSnackbar } from 'notistack'
import requestService from '../../../services/requestService'

const RequestDetail = ({ open, onClose, requestId, onEdit, onRefresh }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [request, setRequest] = useState(null)
  const [auditTrail, setAuditTrail] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', data: {} })

  useEffect(() => {
    if (open && requestId) {
      loadRequestDetails()
      loadAuditTrail()
    }
  }, [open, requestId])

  const loadRequestDetails = async () => {
    setLoading(true)
    try {
      const response = await requestService.getRequestById(requestId)
      if (response.data.success) {
        setRequest(response.data.data)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load request details', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadAuditTrail = async () => {
    try {
      const response = await requestService.getAuditTrail(requestId)
      if (response.data.success) {
        setAuditTrail(response.data.data.auditTrail || [])
      }
    } catch (error) {
      console.error('Error loading audit trail:', error)
    }
  }

  const handleAction = async (actionType, data = {}) => {
    setLoading(true)
    try {
      let response
      switch (actionType) {
        case 'assign':
          response = await requestService.assignRequest(requestId, data)
          break
        case 'fulfill':
          response = await requestService.fulfillRequest(requestId, data)
          break
        case 'receive':
          response = await requestService.receiveRequest(requestId, data)
          break
        case 'reject':
          response = await requestService.rejectRequest(requestId, data)
          break
        case 'close':
          response = await requestService.closeRequest(requestId, data)
          break
        case 'reopen':
          response = await requestService.reopenRequest(requestId, data)
          break
        default:
          throw new Error('Unknown action type')
      }

      if (response.data.success) {
        enqueueSnackbar(`Request ${actionType}ed successfully`, { variant: 'success' })
        loadRequestDetails()
        loadAuditTrail()
        onRefresh && onRefresh()
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || `Failed to ${actionType} request`,
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
      setActionDialog({ open: false, type: '', data: {} })
    }
  }

  const getStatusChip = (status) => {
    const config = {
      PENDING: { color: 'warning', label: 'Pending' },
      PROCESSING: { color: 'info', label: 'Processing' },
      READY: { color: 'primary', label: 'Ready' },
      ISSUED: { color: 'success', label: 'Issued' },
      COMPLETED: { color: 'success', label: 'Completed' },
      REJECTED: { color: 'error', label: 'Rejected' }
    }
    
    const { color, label } = config[status] || { color: 'default', label: status }
    return <Chip label={label} color={color} />
  }

  const getPriorityChip = (priority) => {
    const config = {
      LOW: { color: 'default' },
      MEDIUM: { color: 'primary' },
      HIGH: { color: 'warning' },
      URGENT: { color: 'error' }
    }
    return <Chip label={priority} color={config[priority]?.color || 'default'} variant="outlined" />
  }

  const getTimeStatus = () => {
    if (!request?.requiredByDate) return null
    
    const now = new Date()
    const requiredBy = new Date(request.requiredByDate)
    const daysDiff = Math.ceil((requiredBy - now) / (1000 * 3600 * 24))
    
    if (daysDiff < 0) {
      return { type: 'error', message: `${Math.abs(daysDiff)} days overdue` }
    } else if (daysDiff <= 2) {
      return { type: 'warning', message: `Due in ${daysDiff} days` }
    } else {
      return { type: 'success', message: `${daysDiff} days remaining` }
    }
  }

  const canPerformAction = (action) => {
    if (!request) return false
    
    switch (action) {
      case 'assign':
        return ['PENDING'].includes(request.status)
      case 'fulfill':
        return ['PROCESSING', 'READY'].includes(request.status)
      case 'receive':
        return request.status === 'ISSUED'
      case 'reject':
        return ['PENDING', 'PROCESSING'].includes(request.status)
      case 'close':
        return request.status === 'READY'
      case 'reopen':
        return ['COMPLETED', 'REJECTED'].includes(request.status)
      default:
        return false
    }
  }

  if (!request) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          {loading ? <LinearProgress /> : <Alert severity="error">Request not found</Alert>}
        </DialogContent>
      </Dialog>
    )
  }

  const timeStatus = getTimeStatus()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Request Details - {request.requestNumber}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {getStatusChip(request.status)}
              {getPriorityChip(request.priority)}
              {timeStatus && (
                <Chip 
                  label={timeStatus.message} 
                  color={timeStatus.type} 
                  size="small" 
                />
              )}
            </Box>
          </Box>
          <Box>
            <Tooltip title="Actions">
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreVert />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        <Grid container spacing={3}>
          {/* Request Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 'fit-content' }}>
              <Typography variant="h6" gutterBottom>
                Request Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><Assignment /></ListItemIcon>
                  <ListItemText 
                    primary="Request Type" 
                    secondary={request.requestType?.replace(/_/g, ' ')} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText 
                    primary="Requested By" 
                    secondary={request.requestedBy} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Person /></ListItemIcon>
                  <ListItemText 
                    primary="Requestor Type" 
                    secondary={request.requestorType?.replace(/_/g, ' ')} 
                  />
                </ListItem>
                {request.requestorContact && (
                  <ListItem>
                    <ListItemIcon><Phone /></ListItemIcon>
                    <ListItemText 
                      primary="Contact" 
                      secondary={request.requestorContact} 
                    />
                  </ListItem>
                )}
                {request.requestorEmail && (
                  <ListItem>
                    <ListItemIcon><Email /></ListItemIcon>
                    <ListItemText 
                      primary="Email" 
                      secondary={request.requestorEmail} 
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemIcon><CalendarToday /></ListItemIcon>
                  <ListItemText 
                    primary="Request Date" 
                    secondary={format(parseISO(request.requestDate), 'PPP')} 
                  />
                </ListItem>
                {request.requiredByDate && (
                  <ListItem>
                    <ListItemIcon><CalendarToday /></ListItemIcon>
                    <ListItemText 
                      primary="Required By" 
                      secondary={format(parseISO(request.requiredByDate), 'PPP')} 
                    />
                  </ListItem>
                )}
                {request.charges > 0 && (
                  <ListItem>
                    <ListItemIcon><AttachMoney /></ListItemIcon>
                    <ListItemText 
                      primary="Charges" 
                      secondary={`₹${request.charges}`} 
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Grid>

          {/* MRD Files */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 'fit-content' }}>
              <Typography variant="h6" gutterBottom>
                Requested Files ({request.mrdFiles?.length || 0})
              </Typography>
              <List dense>
                {request.mrdFiles?.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={file.ipdNumber}
                      secondary={file.patientName}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Purpose & Details */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Purpose & Details
              </Typography>
              {request.purpose && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Purpose:
                  </Typography>
                  <Typography variant="body2">
                    {request.purpose}
                  </Typography>
                </Box>
              )}
              {request.requestDetails && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Details:
                  </Typography>
                  <Typography variant="body2">
                    {request.requestDetails}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Audit Trail */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Activity Timeline
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {auditTrail.map((entry, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {entry.action.replace(/_/g, ' ')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(parseISO(entry.at), 'MMM dd, yyyy HH:mm')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        By: {entry.by?.name || 'System'}
                      </Typography>
                      {entry.remarks && (
                        <Typography variant="body2">
                          {entry.remarks}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {auditTrail.length === 0 && (
                  <Alert severity="info">
                    No activity recorded yet
                  </Alert>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        {onEdit && (
          <Button variant="outlined" onClick={() => onEdit(request)}>
            Edit Request
          </Button>
        )}
      </DialogActions>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {canPerformAction('assign') && (
          <MenuItem onClick={() => {
            setActionDialog({ open: true, type: 'assign', data: {} })
            setAnchorEl(null)
          }}>
            <MenuListItemIcon><Person /></MenuListItemIcon>
            Assign Request
          </MenuItem>
        )}
        
        {canPerformAction('fulfill') && (
          <MenuItem onClick={() => {
            setActionDialog({ open: true, type: 'fulfill', data: {} })
            setAnchorEl(null)
          }}>
            <MenuListItemIcon><CheckCircle /></MenuListItemIcon>
            Fulfill Request
          </MenuItem>
        )}
        
        {canPerformAction('receive') && (
          <MenuItem onClick={() => {
            setActionDialog({ open: true, type: 'receive', data: {} })
            setAnchorEl(null)
          }}>
            <MenuListItemIcon><Receipt /></MenuListItemIcon>
            Receive Files
          </MenuItem>
        )}
        
        {canPerformAction('reject') && (
          <MenuItem onClick={() => {
            setActionDialog({ open: true, type: 'reject', data: {} })
            setAnchorEl(null)
          }}>
            <MenuListItemIcon><Cancel /></MenuListItemIcon>
            Reject Request
          </MenuItem>
        )}
        
        {canPerformAction('close') && (
          <MenuItem onClick={() => {
            setActionDialog({ open: true, type: 'close', data: {} })
            setAnchorEl(null)
          }}>
            <MenuListItemIcon><CheckCircle /></MenuListItemIcon>
            Close Request
          </MenuItem>
        )}
        
        {canPerformAction('reopen') && (
          <MenuItem onClick={() => {
            setActionDialog({ open: true, type: 'reopen', data: {} })
            setAnchorEl(null)
          }}>
            <MenuListItemIcon><Refresh /></MenuListItemIcon>
            Reopen Request
          </MenuItem>
        )}
      </Menu>

      {/* Action Dialog */}
      <Dialog 
        open={actionDialog.open} 
        onClose={() => setActionDialog({ open: false, type: '', data: {} })}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {actionDialog.type.charAt(0).toUpperCase() + actionDialog.type.slice(1)} Request
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Remarks"
            value={actionDialog.data.remarks || ''}
            onChange={(e) => setActionDialog(prev => ({
              ...prev,
              data: { ...prev.data, remarks: e.target.value }
            }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: '', data: {} })}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleAction(actionDialog.type, actionDialog.data)}
            disabled={loading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default RequestDetail